// Preview renders of the draggable node components for the left Nodes panel.
// Each one mounts the actual node component (not a mockup) inside its own
// ReactFlowProvider so xyflow's Handle works, then scales the result to fit
// the narrow panel. Pointer events are disabled at the wrapper so the
// preview cannot be interacted with — drag-and-drop still works because the
// outer card owns the drag handlers.

import { useMemo } from 'react';
import { ReactFlowProvider, Position } from '@xyflow/react';

import TimelineViewerNode from '../../components/Ubiverse/nodes/TimelineViewerNode';
import SimulatorNode from '../../components/Ubiverse/nodes/SimulatorNode';
import ButtonNode from '../../components/Ubiverse/nodes/ButtonNode';

import { initialGlobalState } from '../../states/GlobalStateReducer';
import { UbiverseClient, UbiverseNodeType__Child } from '../../types';
import { RealtimeClient } from '../../services/realtime-client';

const PREVIEW_SEQUENCE_ID = 'preview-sequence';

const noopDispatch = () => {};

const noopRealtimeClient = {
  emitEvent: () => {},
  subscribeEvent: () => {},
  unsubscribeEvent: () => {},
} as unknown as RealtimeClient;

const previewSessionList: UbiverseClient[] = [
  {
    id: 'preview-device-01',
    type: UbiverseNodeType__Child,
    auth: { isAuthenticated: true },
    data: { label: 'ESP32 Finger' },
  },
];

const baseNodeProps = {
  type: '__preview',
  dragging: false,
  isConnectable: false,
  positionAbsoluteX: 0,
  positionAbsoluteY: 0,
  selected: false,
  deletable: false,
  draggable: false,
  selectable: false,
  zIndex: 0,
  targetPosition: Position.Left,
  sourcePosition: Position.Right,
};

/**
 * Wraps a node render in the context xyflow expects (Handle requires this)
 * and scales it down to fit a narrow panel slot.
 */
const PreviewFrame = ({ children, scale = 0.36, height = 120 }: { children: React.ReactNode; scale?: number; height?: number }) => (
  <div className="node-preview-frame" style={{ height }}>
    <div
      className="node-preview-frame__inner"
      style={{
        transform: `scale(${scale})`,
        transformOrigin: 'top left',
        pointerEvents: 'none',
        userSelect: 'none',
      }}
    >
      <ReactFlowProvider>{children}</ReactFlowProvider>
    </div>
  </div>
);

export const TimelineViewerNodePreview = () => {
  const previewState = useMemo(() => ({
    ...initialGlobalState,
    sequences: [{
      id: PREVIEW_SEQUENCE_ID,
      name: 'Preview',
      isMuted: false,
      notes: [
        { id: 'n1', position: 1, intensity: 6 },
        { id: 'n2', position: 3, intensity: 8 },
        { id: 'n3', position: 5, intensity: 4 },
        { id: 'n4', position: 7, intensity: 7 },
      ],
    }],
  }), []);

  const data = useMemo(() => ({
    id: PREVIEW_SEQUENCE_ID,
    label: 'Timeline Sequence',
    onClick: () => {},
    toggleEditMode: () => {},
    state: previewState,
    dispatch: noopDispatch,
    realtimeClient: noopRealtimeClient,
  }), [previewState]);

  return (
    <PreviewFrame scale={0.32}>
      <TimelineViewerNode
        {...baseNodeProps}
        id={PREVIEW_SEQUENCE_ID}
        data={data as any}
      />
    </PreviewFrame>
  );
};

export const SimulatorNodePreview = () => {
  const data = useMemo(() => ({
    label: 'Device',
    sessionList: previewSessionList,
    selectedClientId: previewSessionList[0].id,
    onSelectClient: () => {},
  }), []);

  return (
    <PreviewFrame scale={0.55} height={110}>
      <SimulatorNode
        {...baseNodeProps}
        id="preview-simulator"
        data={data as any}
      />
    </PreviewFrame>
  );
};

export const ButtonNodePreview = () => {
  const data = useMemo(() => ({
    label: 'Trigger',
    onClick: () => {},
  }), []);

  return (
    <PreviewFrame scale={0.55} height={140}>
      <ButtonNode
        {...baseNodeProps}
        id="preview-button"
        data={data as any}
      />
    </PreviewFrame>
  );
};
